import { Injectable } from '@angular/core';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { ResponseDto } from 'src/app/models/dto/responseDto';
import { PaginatedItemsResponseDto } from 'src/app/models/dto/paginatedItemsResponseDto';
import { MigrationEntity } from './migration-config.service';

/**
 * Raw response shapes from the Go Lambda API.
 */
export interface GoLambdaLockInfo {
  LockedBy: string;
  LockedAt: string;
}

export interface GoLambdaModel {
  Id: string;
  Data: string; // JSON string
  LockInfo?: GoLambdaLockInfo;
}

export interface GoLambdaPage {
  Items: GoLambdaModel[];
  TotalCount: number;
  CurrentPage: number;
  PageSize: number;
  TotalPages: number;
}

/**
 * Maps Go Lambda API responses to the Config client's existing DTO formats.
 *
 * Go Lambda stores data as camelCase JSONB. The old C# API uses PascalCase.
 * This mapper bridges the two formats so downstream code sees no difference.
 */
@Injectable({
  providedIn: 'root'
})
export class GoLambdaMapperService {

  /**
   * Convert a Go Lambda getById response to the Config client's ResponseDto format.
   * Mirrors what convertGraphqlResponseToDto() produces from the old API.
   */
  toResponseDto<T>(model: GoLambdaModel): ResponseDto {
    if (!model) { console.warn('[Migration Mapper] toResponseDto: model is null'); return { success: false, returnValue: null, responseMessages: ['Not found'] }; }

    console.log('[Migration Mapper] toResponseDto raw Data type:', typeof model.Data, 'preview:', typeof model.Data === 'string' ? model.Data.substring(0, 200) : JSON.stringify(model.Data).substring(0, 200));
    const data = typeof model.Data === 'string' ? JSON.parse(model.Data) : model.Data;
    // A model that arrives with no Data is not an item. AppSync coerces any non-item payload
    // onto the item type, so a lambda error envelope comes back as { Id: null, Data: null,
    // LockInfo: null } — truthy, but empty. Dereferencing it threw
    // "Cannot set properties of null (setting 'lockedBy')" and took the whole page down,
    // hiding whatever the server was actually trying to say. Report it instead.
    if (data == null) {
      console.warn('[Migration Mapper] toResponseDto: model has no Data', model);
      return { success: false, returnValue: null, responseMessages: ['The server returned no data for this item.'] };
    }
    this.applyGoToDtoFieldMapping(data);
    // Set lock info from the separate LockInfo field (JOIN with item_locks)
    console.log('[Migration Mapper] toResponseDto LockInfo:', model.LockInfo);
    data.lockedBy = model.LockInfo?.LockedBy ?? '';
    data.lockedTime = model.LockInfo?.LockedAt ?? null;
    console.log('[Migration Mapper] toResponseDto mapped keys:', Object.keys(data));

    return {
      success: true,
      returnValue: data as T,
      responseMessages: []
    };
  }

  /**
   * Convert a Go Lambda paginated response to Config client's PaginatedItemsResponseDto.
   */
  toPaginatedDto(page: GoLambdaPage): PaginatedItemsResponseDto {
    if (!page) { console.warn('[Migration Mapper] toPaginatedDto: page is null/undefined'); return { totalCount: 0, items: [] }; }

    console.log('[Migration Mapper] toPaginatedDto raw page:', JSON.stringify({ TotalCount: page.TotalCount, ItemCount: page.Items?.length, firstItem: page.Items?.[0] }));
    const result = {
      totalCount: page.TotalCount,
      items: page.Items?.map(item => this.toBaseTestItemDto(item)).filter(item => item?.uid && item?.name) ?? []
    };
    console.log('[Migration Mapper] toPaginatedDto mapped:', JSON.stringify({ totalCount: result.totalCount, itemCount: result.items?.length, firstItem: result.items?.[0] }));
    return result;
  }

  /**
   * Convert a Go Lambda model to BaseTestItemDto (for list views).
   */
  toBaseTestItemDto(model: GoLambdaModel): BaseTestItemDto {
    if (!model) { console.warn('[Migration Mapper] toBaseTestItemDto: model is null'); return undefined; }

    console.log('[Migration Mapper] toBaseTestItemDto raw model:', JSON.stringify({ Id: model.Id, DataType: typeof model.Data, DataPreview: typeof model.Data === 'string' ? model.Data.substring(0, 200) : model.Data }));
    const data = typeof model.Data === 'string' ? JSON.parse(model.Data) : model.Data;
    // Same guard as toResponseDto: an item-shaped response with null Data is not an item.
    if (data == null) { console.warn('[Migration Mapper] toBaseTestItemDto: model has no Data', model); return undefined; }
    console.log('[Migration Mapper] toBaseTestItemDto parsed keys:', Object.keys(data));
    return {
      uid: data.uid,
      itemNo: data.itemNo,
      name: data.name,
      version: data.version,
      createdBy: data.createdBy,
      creationTime: this.parseDate(data.creationTime),
      modificationTime: this.parseDate(data.modificationTime),
      modifiedBy: data.modifiedBy,
      comment: data.comment ?? '',
      lockedBy: model.LockInfo?.LockedBy ?? '',
      lockedTime: this.parseDate(model.LockInfo?.LockedAt),
      // debug: remove after lock issue is resolved
      approvalStatus: data.approval?.status ?? data.approvalStatus ?? '',
      approvedForTestLevel: data.approvedForTestLevel ?? '',
      reviewApprovalStatus: data.approval?.status ?? data.reviewApprovalStatus,
      reviewApprovalId: data.approval?.id?.toString() ?? data.reviewApprovalId,
      reviewApprovalItemVersion: data.approval?.itemVersion ?? data.reviewApprovalItemVersion,
    };
  }

  /**
   * Convert a Go Lambda model's Data to a PascalCase object matching what the old C# API returns.
   * This is used for the full object (getByUid) where the consumer expects PascalCase field names.
   */
  toOldFormatData(model: GoLambdaModel): any {
    if (!model) return null;
    const data = typeof model.Data === 'string' ? JSON.parse(model.Data) : model.Data;
    this.applyGoToDtoFieldMapping(data);
    return data;
  }

  /**
   * Convert a Config client DTO (PascalCase) to Go Lambda format (camelCase JSON string).
   * Used for create/update mutations.
   *
   * isUpdate: the Go update merges the payload onto the stored record, so a field left out
   * of the payload keeps its old value. On update, cleared fields are therefore sent as
   * ''/[] instead of being stripped (updates are not schema-validated, so minLength doesn't apply).
   */
  fromDtoToGoData(dto: any, entity?: MigrationEntity, isUpdate = false): string {
    const mapped = this.pascalToCamelDeep(dto);
    this.applyReverseFieldNameMapping(mapped, entity);
    this.cleanForGoSchema(mapped, entity, isUpdate);
    return JSON.stringify(mapped);
  }

  /**
   * Convert a DTO to a Go Lambda search criteria JSON string.
   * Only includes fields with meaningful values — empty strings, empty arrays,
   * false booleans, zero numbers, and empty GUIDs are stripped.
   * No defaults are added (unlike fromDtoToGoData which is for create/update).
   */
  fromDtoToSearchData(dto: any, entity?: MigrationEntity, scope?: 'ALL' | 'MINE' | 'MY_GROUP'): string {
    const mapped = this.pascalToCamelDeep(dto);
    this.applyReverseFieldNameMapping(mapped, entity);

    // The lambdas resolve the scope from the caller (created_by = caller for MINE, the
    // caller's groups against functionalGroupOwners for MY_GROUP, case-insensitive). The old
    // API expresses the same intent as createdBy / ownerIds in the DTO, which on the Go path
    // would be a substring match and a case-sensitive one; the scope condition replaces them.
    if (scope === 'MINE') delete mapped.createdBy;
    if (scope === 'MY_GROUP') delete mapped.functionalGroupOwners;

    // SW version coverage: the search form hands over the ECU as a bare string (or '').
    // Go matches arrays element-exact with contains-all, so send real string arrays;
    // empty ones are dropped by the strip loop below like any other empty criterion.
    if (entity === 'softwareVersionCoverage') {
      mapped.ecuIds = this.toStringList(mapped.ecuIds);
      mapped.softwareVersions = this.toStringList(mapped.softwareVersions);
    }

    // Flatten alternative to root (same as cleanForGoSchema)
    if (mapped.alternative && typeof mapped.alternative === 'object') {
      const alt = mapped.alternative;
      if (alt.ownerIds?.length > 0) mapped.functionalGroupOwners = alt.ownerIds;
      if (alt.testedByGroupIds?.length > 0) mapped.testedByGroups = alt.testedByGroupIds;
      if (alt.testLevelIds?.length > 0) mapped.testLevel = alt.testLevelIds[0];
      if (alt.sopStart) mapped.sopStart = alt.sopStart;
      if (alt.sopEnd) mapped.sopEnd = alt.sopEnd;
      if (alt.environment) mapped.environment = alt.environment;
      if (alt.fpc && alt.fpc !== '[]') mapped.fpc = alt.fpc;
      if (alt.testGroups?.length > 0) mapped.testGroups = alt.testGroups;
      if (alt.testObjects?.length > 0) mapped.testObjects = alt.testObjects;
      if (alt.testCases?.length > 0) mapped.testCases = alt.testCases;
    }
    delete mapped.alternative;

    // Free-text search: match the item number OR the name via the lambdas' reserved
    // `or` group (buildGenericSearchWhere). Replaces itemNo/name when present.
    if (typeof mapped.searchText === 'string' && mapped.searchText.trim() !== '') {
      const text = mapped.searchText.trim();
      mapped.or = [{ itemNo: text }, { name: text }];
      delete mapped.itemNo;
      delete mapped.name;
    }
    delete mapped.searchText;

    // Vehicle functions: the TC/TS builders match an object array by containment
    // (any of the given objects), so send only the ids, as integers like the stored
    // shape — a full {id, name, revision} object would demand an exact element match.
    if (Array.isArray(mapped.vehicleFunctions)) {
      mapped.vehicleFunctions = mapped.vehicleFunctions
        .map((vf: any) => this.toIntOrKeep(vf?.id))
        .filter((id: any) => id !== undefined && id !== null && id !== '')
        .map((id: any) => ({ id }));
    }

    // Linked-item refs (TA testCases, TSpec testAnalyses): every lambda matches an object
    // array by containment (any of the given objects), so send bare {uid} refs, with the
    // version only when the form set one. The form value is a whole item, which no stored
    // ref would ever equal.
    const refKey = entity === 'testAnalyses' ? 'testCases' : entity === 'testSpecifications' ? 'testAnalyses' : undefined;
    if (refKey && Array.isArray(mapped[refKey])) {
      mapped[refKey] = mapped[refKey]
        .filter((x: any) => x && typeof x === 'object' && x.uid)
        .map((x: any) => (typeof x.version === 'number' && x.version > 0 ? { uid: x.uid, version: x.version } : { uid: x.uid }));
    }

    // Strip empty/default values — only keep meaningful search criteria
    const emptyGuid = '00000000-0000-0000-0000-000000000000';
    const result: any = {};
    for (const [key, val] of Object.entries(mapped)) {
      if (val === null || val === undefined) continue;
      if (typeof val === 'string' && (val === '' || val === emptyGuid)) continue;
      if (typeof val === 'number' && val === 0) continue;
      if (typeof val === 'boolean' && val === false) continue;
      if (Array.isArray(val) && (val.length === 0 || (val.length === 1 && Array.isArray(val[0])))) continue;
      result[key] = val;
    }

    return JSON.stringify(result);
  }

  /**
   * Remove fields the Go Lambda schema doesn't accept (additionalProperties: false)
   * and ensure required fields have valid values.
   */
  private cleanForGoSchema(obj: any, entity?: MigrationEntity, isUpdate = false): void {
    if (!obj || typeof obj !== 'object') return;

    // Send cleared values on update (see fromDtoToGoData). Limited to the four test item
    // types, whose update DTO is always built from the full form.
    const sendClearedValues = isUpdate &&
      (entity === 'testCases' || entity === 'testSuites' || entity === 'testAnalyses' || entity === 'testSpecifications');

    // --- Flatten alternative to root (Go stores these flat) ---
    if (obj.alternative && typeof obj.alternative === 'object') {
      const alt = obj.alternative;
      // TA/TC/TS: ownerIds from alternative → functionalGroupOwners at root
      if (alt.ownerIds?.length > 0 || (sendClearedValues && Array.isArray(alt.ownerIds))) {
        obj.functionalGroupOwners = alt.ownerIds;
      }
      if (alt.testedByGroupIds !== undefined) obj.testedByGroups = alt.testedByGroupIds;
      if (alt.testLevelIds !== undefined) {
        obj.testLevel = Array.isArray(alt.testLevelIds) ? alt.testLevelIds[0] ?? '' : alt.testLevelIds;
      }
      if (alt.testCases !== undefined && !obj.testCases) obj.testCases = alt.testCases;
      if (alt.sopStart !== undefined) obj.sopStart = alt.sopStart;
      if (alt.sopEnd !== undefined) obj.sopEnd = alt.sopEnd;
      if (alt.specifications !== undefined) obj.specifications = alt.specifications;
      // TA delegated test levels live under alternative.excludedFromTest → Go root delegatedToTestLevels
      if (alt.excludedFromTest?.delegatedToTestLevelIds !== undefined) {
        obj.delegatedToTestLevels = alt.excludedFromTest.delegatedToTestLevelIds;
      } else if (sendClearedValues && entity === 'testAnalyses') {
        // The TA factory omits excludedFromTest once every delegated level is removed
        obj.delegatedToTestLevels = [];
      }
      // TC alternative fields → root
      if (alt.environment !== undefined) obj.environment = alt.environment;
      if (alt.fpc !== undefined) obj.fpc = alt.fpc;
      if (alt.tpc !== undefined) obj.tpc = alt.tpc;
      if (alt.testGroups !== undefined) obj.testGroups = alt.testGroups;
      if (alt.testObjects !== undefined) obj.testObjects = alt.testObjects;
      if (alt.gitLabScript !== undefined) obj.gitLabScripts = alt.gitLabScript;
      if (alt.gitLabScripts !== undefined) obj.gitLabScripts = alt.gitLabScripts;
      if (alt.perforceScripts !== undefined) obj.perforceScripts = alt.perforceScripts;
    }

    // Remove fields not in any Go schema
    // (vehicleFunctions IS now in the Go TC schema, so it's kept and round-trips.)
    // lockedBy / lockedTime: locks live in item_locks and come back as LockInfo; the Go
    // schemas no longer accept them in the item JSON (additionalProperties: false), so
    // they are stripped unconditionally — including when the caller holds the lock.
    const fieldsToRemove = [
      'approvalStatus', 'approvedForTestLevel',
      'reviewApprovalStatus', 'reviewApprovalId', 'reviewApprovalItemVersion',
      'approval', 'ownerIds',
      'alternative', 'tpc',
      'excludedFromTest',
      'testedByGroupIds', 'testLevelIds',
      'lockedBy', 'lockedTime',
      'searchText',
    ];
    for (const f of fieldsToRemove) {
      delete obj[f];
    }

    // tags: array of non-empty strings (Go schema items require minLength 1).
    if (Array.isArray(obj.tags)) {
      obj.tags = obj.tags.filter((t: any) => typeof t === 'string' && t.trim() !== '');
    }

    // Remove empty strings for optional fields with minLength: 1 (kept on update to clear them)
    const minLengthFields = [
      'derivedFrom', 'testType', 'objectUnderTestType', 'environment',
      'label', 'comment', 'description',
      'impactIfFailureAppears', 'probabilityOfFailure', 'rationaleForRiskAssessment',
      'testLevel',
    ];
    if (!sendClearedValues) {
      for (const f of minLengthFields) {
        if (obj[f] === '') delete obj[f];
      }
    }
    // Ensure required fields have values
    if (!obj.version) obj.version = 1;
    if (!obj.createdBy) obj.createdBy = 'system';
    if (!obj.modifiedBy) obj.modifiedBy = obj.createdBy;
    // itemNo is intentionally NOT defaulted here. The Go lambdas auto-generate
    // itemNo from a per-entity Postgres sequence (TCA-/TSU-/TAN-/TSP-{n}) when
    // the incoming value is empty or missing. See GO-API-MIGRATION.md §4d.
    // Setting itemNo = uid here would suppress generation (Go honours any
    // non-empty incoming value) and we'd end up with the uid as the label.

    // Empty fpc — stripped on create (schema expects string with minLength: 1), sent as '' on update
    if (Array.isArray(obj.fpc) && obj.fpc.length === 0) obj.fpc = '';
    if (obj.fpc === '' && !sendClearedValues) delete obj.fpc;

    // sopStart/sopEnd are SOP production-period codes (e.g. '2810'), not calendar dates.
    // Sent through as plain strings; the Go schema types them as string (not format:date).
    // Only strip empties (minLength concerns), and not on update.
    if (!sendClearedValues) {
      if (obj.sopStart === '') delete obj.sopStart;
      if (obj.sopEnd === '') delete obj.sopEnd;
    }

    // vehicleFunctions: accepted by the Go TC and TS schemas (with integer id/revision). The form
    // supplies string ids, so coerce them for those entities.
    if (Array.isArray(obj.vehicleFunctions) && (entity === 'testCases' || entity === 'testSuites')) {
      obj.vehicleFunctions = obj.vehicleFunctions.map((vf: any) => this.coerceVehicleFunctionIds(vf));
    }

    // arpyLinks: the factory leaves it undefined when the user removed every link (see
    // test-case.factory.ts - sending [] to the old API is unconfirmed). The Go update merges,
    // so a missing key would keep the stored links; send [] to clear them, as the old API's
    // full link rewrite does.
    if (sendClearedValues && entity === 'testCases' && obj.arpyLinks === undefined) {
      obj.arpyLinks = [];
    }

    // Clean TS hierarchy links (testCases, testSuites) — strip name/itemNo, convert value[]→string
    if (Array.isArray(obj.testCases) && obj.testCases[0]?.testItemUid !== undefined) {
      obj.testCases = obj.testCases.map((l: any) => this.cleanHierarchyLink(l));
    }
    if (Array.isArray(obj.testSuites)) {
      obj.testSuites = obj.testSuites.map((l: any) => this.cleanHierarchyLink(l));
    }

    // Clean TA testCases — Go expects TestCaseRef {uid, version} only
    if (Array.isArray(obj.testCases) && obj.testCases[0]?.uid !== undefined) {
      obj.testCases = obj.testCases.map((tc: any) => ({ uid: tc.uid, version: tc.version ?? 0 }));
    }

    // Clean TSpec testAnalyses — Go expects TestAnalysisRef {uid, version} only
    if (Array.isArray(obj.testAnalyses) && obj.testAnalyses[0]?.uid !== undefined) {
      obj.testAnalyses = obj.testAnalyses.map((ta: any) => ({ uid: ta.uid, version: ta.version ?? 0 }));
    }

    // --- TA-specific cleaning (Go schema is strict: additionalProperties:false) ---

    // totalRiskLevel: DTO carries the raw form-control object; Go expects a plain string.
    if (obj.totalRiskLevel != null && typeof obj.totalRiskLevel === 'object') {
      obj.totalRiskLevel = obj.totalRiskLevel.value ?? obj.totalRiskLevel.name ?? obj.totalRiskLevel.key ?? '';
    }

    // requirements & specifications: sent in full. The Go TA schema stores the whole Polarion
    // snapshot (asil/status/maturityStatus, the linkedRequirement object, etc.) via
    // additionalProperties, so the UI can render them after saving. No stripping here.

    // --- SW version coverage ---
    // The factory hands the ECU over as a single string (the old GraphQL input relied on
    // list coercion to wrap it); the Go schema requires string arrays for both lists, with
    // at least one entry each. An empty list is left empty so the server's validation
    // message reaches the user instead of a silently-invented value.
    if (entity === 'softwareVersionCoverage') {
      obj.ecuIds = this.toStringList(obj.ecuIds);
      obj.softwareVersions = this.toStringList(obj.softwareVersions);
    }
  }

  /**
   * Normalize a form value into a list of non-empty strings: a bare string becomes a
   * one-element list, dropdown option objects contribute their `value` (or `key`), and
   * blanks are dropped. Values are otherwise passed through verbatim — software versions
   * are matched exactly against test runs, so they must not be trimmed or case-folded.
   */
  private toStringList(value: any): string[] {
    const list = Array.isArray(value) ? value : (value == null || value === '' ? [] : [value]);
    return list
      .map((v: any) => (v && typeof v === 'object') ? (v.value ?? v.key ?? '') : v)
      .filter((v: any) => typeof v === 'string' && v.trim() !== '');
  }

  /**
   * Coerce vehicleFunction id/revision (and its sub-functions') to integers for the Go schema.
   */
  private coerceVehicleFunctionIds(vf: any): any {
    if (!vf || typeof vf !== 'object') return vf;
    const out = { ...vf };
    out.id = this.toIntOrKeep(out.id);
    if (out.revision !== undefined) out.revision = this.toIntOrKeep(out.revision);
    if (Array.isArray(out.vehicleSubFunctions)) {
      out.vehicleSubFunctions = out.vehicleSubFunctions.map((sf: any) => {
        if (!sf || typeof sf !== 'object') return sf;
        const s = { ...sf };
        s.id = this.toIntOrKeep(s.id);
        if (s.revision !== undefined) s.revision = this.toIntOrKeep(s.revision);
        return s;
      });
    }
    return out;
  }

  /**
   * Convert a value to a number when it parses cleanly; otherwise leave it unchanged
   * (empty/null/undefined pass through so required-field validation still fires).
   */
  private toIntOrKeep(v: any): any {
    if (v === undefined || v === null || v === '') return v;
    const n = Number(v);
    return Number.isNaN(n) ? v : n;
  }

  /**
   * Strip hierarchy link items to only Go-schema-allowed fields.
   * value: string[] → one comma-joined string, exactly like C# (string.Join(",", Value)); remove name, itemNo.
   * The Executor compares ConditionValue against the answer, so the separator must match C#.
   */
  private cleanHierarchyLink(link: any): any {
    if (!link) return link;
    const clean: any = {
      testItemUid: link.testItemUid,
      version: link.version ?? 0,
    };
    if (link.operator) clean.operator = link.operator;
    if (link.value != null) clean.value = Array.isArray(link.value) ? link.value.join(',') : link.value;
    if (link.childrenTestItemLinks?.length > 0) {
      clean.childrenTestItemLinks = link.childrenTestItemLinks.map((c: any) => this.cleanHierarchyLink(c));
    }
    return clean;
  }

  /**
   * Convert a DTO to the Go Lambda SearchInput format.
   * Extracts only the fields the Go schema accepts, drops empty strings and empty arrays.
   */
  toSearchInput(entity: MigrationEntity, dto: any): any {
    const input: any = {};
    const emptyGuid = '00000000-0000-0000-0000-000000000000';
    // Common fields across all search inputs
    if (dto.uid && dto.uid !== emptyGuid) input.uid = dto.uid;
    if (dto.itemNo) input.itemNo = dto.itemNo;
    if (dto.name) input.name = dto.name;
    if (dto.createdBy) input.createdBy = dto.createdBy;
    if (dto.approvalStatus) input.approvalStatus = dto.approvalStatus;
    if (dto.approvedForTestLevel) input.approvedForTestLevel = dto.approvedForTestLevel;
    if (dto.label) input.label = dto.label;
    if (dto.tags?.length > 0) input.tags = dto.tags;

    // TC & TS: ownerIds (from dto.ownerIds or dto.alternative?.ownerIds)
    if (entity === 'testCases' || entity === 'testSuites') {
      const owners = dto.ownerIds ?? dto.alternative?.ownerIds;
      if (owners?.length > 0) input.ownerIds = owners;
    }

    // TA: totalRiskLevel instead of ownerIds
    if (entity === 'testAnalyses') {
      if (dto.totalRiskLevel) input.totalRiskLevel = dto.totalRiskLevel;
    }

    return input;
  }

  /**
   * Recursively convert camelCase keys to PascalCase.
   */
  private camelToPascalDeep(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) return obj.map(item => this.camelToPascalDeep(item));
    if (typeof obj !== 'object') return obj;

    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
      result[pascalKey] = this.camelToPascalDeep(value);
    }
    return result;
  }

  /**
   * Recursively convert PascalCase keys to camelCase.
   */
  private pascalToCamelDeep(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) return obj.map(item => this.pascalToCamelDeep(item));
    if (typeof obj !== 'object') return obj;

    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      result[camelKey] = this.pascalToCamelDeep(value);
    }
    return result;
  }

  /**
   * Go stores a hierarchy link condition value as one comma-joined string (same as C#,
   * which splits on ',' when reading); the UI works with string[]. Older Go rows may
   * still hold the raw array.
   */
  private hierarchyValueToArray(value: any): string[] {
    if (value == null || value === '') return [];
    if (Array.isArray(value)) return value.map((v: any) => String(v));
    return String(value).split(',').map(v => v.trim()).filter(v => !!v);
  }

  /**
   * Normalize a Go Lambda hierarchy link item.
   * Go enriches links with a `data` wrapper containing the full item.
   * The UI expects `name`, `itemNo`, `testItemUid` at the top level.
   */
  private normalizeHierarchyLink(link: any): any {
    if (!link) return link;
    // If the link has a `data` wrapper, pull name/itemNo/uid from it
    if (link.data && typeof link.data === 'object') {
      return {
        testItemUid: link.testItemUid ?? link.data.uid ?? '',
        name: link.name ?? link.data.name ?? '',
        itemNo: link.itemNo ?? link.data.itemNo ?? '',
        version: link.version ?? link.data.version ?? 0,
        operator: link.operator ?? '',
        value: this.hierarchyValueToArray(link.value),
        childrenTestItemLinks: (link.childrenTestItemLinks ?? []).map((c: any) => this.normalizeHierarchyLink(c)),
      };
    }
    // Already flat — ensure all fields exist
    return {
      testItemUid: link.testItemUid ?? '',
      name: link.name ?? '',
      itemNo: link.itemNo ?? '',
      version: link.version ?? 0,
      operator: link.operator ?? '',
      value: this.hierarchyValueToArray(link.value),
      childrenTestItemLinks: (link.childrenTestItemLinks ?? []).map((c: any) => this.normalizeHierarchyLink(c)),
    };
  }

  /**
   * Apply Go -> C# field name mappings that aren't simple casing changes.
   * Operates on a PascalCased object (after camelToPascal conversion).
   */
  private applyFieldNameMapping(obj: any): void {
    if (!obj || typeof obj !== 'object') return;

    // functionalGroupOwners -> OwnerIds
    if (obj.FunctionalGroupOwners !== undefined) {
      obj.OwnerIds = obj.FunctionalGroupOwners;
      delete obj.FunctionalGroupOwners;
    }

    // ecus -> EcuIds
    if (obj.Ecus !== undefined) {
      obj.EcuIds = obj.Ecus;
      delete obj.Ecus;
    }

    // offboardComponents -> OffboardComponentIds
    if (obj.OffboardComponents !== undefined) {
      obj.OffboardComponentIds = obj.OffboardComponents;
      delete obj.OffboardComponents;
    }

    // productProperties -> ProductPropertyIds
    if (obj.ProductProperties !== undefined) {
      obj.ProductPropertyIds = obj.ProductProperties;
      delete obj.ProductProperties;
    }

    // Nest flat fields into Alternative (for test cases)
    if (obj.Environment !== undefined || obj.Fpc !== undefined || obj.TestGroups !== undefined || obj.TestObjects !== undefined || obj.GitLabScripts !== undefined) {
      if (!obj.Alternative) obj.Alternative = {};
      if (obj.Environment !== undefined) { obj.Alternative.Environment = obj.Environment; delete obj.Environment; }
      if (obj.Fpc !== undefined) { obj.Alternative.Fpc = obj.Fpc; delete obj.Fpc; }
      if (obj.TestGroups !== undefined) { obj.Alternative.TestGroups = obj.TestGroups; delete obj.TestGroups; }
      if (obj.TestObjects !== undefined) { obj.Alternative.TestObjects = obj.TestObjects; delete obj.TestObjects; }
      if (obj.GitLabScripts !== undefined) { obj.Alternative.GitLabScript = obj.GitLabScripts; delete obj.GitLabScripts; }
    }
  }

  /**
   * Map Go Lambda camelCase data to DTO camelCase format.
   * Handles field renames, structural differences (Alternative nesting),
   * and defaults missing arrays/objects so components don't crash.
   */
  private applyGoToDtoFieldMapping(obj: any): void {
    if (!obj || typeof obj !== 'object') return;

    // --- Field renames (all entities) ---
    if (obj.functionalGroupOwners !== undefined) {
      obj.ownerIds = obj.functionalGroupOwners;
      delete obj.functionalGroupOwners;
    }
    if (obj.ecus !== undefined) {
      obj.ecuIds = obj.ecus;
      delete obj.ecus;
    }
    if (obj.offboardComponents !== undefined) {
      obj.offboardComponentIds = obj.offboardComponents;
      delete obj.offboardComponents;
    }
    if (obj.productProperties !== undefined) {
      obj.productPropertyIds = obj.productProperties;
      delete obj.productProperties;
    }

    // --- TC: nest flat fields into alternative ---
    const tcAltFields = ['environment', 'fpc', 'tpc', 'testGroups', 'testObjects', 'gitLabScripts', 'perforceScripts'];
    // Detect TC by inputTypeName (TC-specific field)
    if (obj.inputTypeName !== undefined) {
      if (!obj.alternative) obj.alternative = { uid: '' };
      for (const f of tcAltFields) {
        if (obj[f] !== undefined) { obj.alternative[f === 'gitLabScripts' ? 'gitLabScript' : f] = obj[f]; delete obj[f]; }
      }
      obj.alternative.environment = obj.alternative.environment ?? '';
      obj.alternative.fpc = obj.alternative.fpc ?? '';
      obj.alternative.tpc = obj.alternative.tpc ?? '';
      obj.alternative.testGroups = obj.alternative.testGroups ?? [];
      obj.alternative.testObjects = obj.alternative.testObjects ?? [];
      obj.alternative.gitLabScript = obj.alternative.gitLabScript ?? [];
      obj.alternative.perforceScripts = obj.alternative.perforceScripts ?? [];
      // TC defaults
      obj.steps = obj.steps ?? [];
      obj.ecuIds = obj.ecuIds ?? [];
      obj.offboardComponentIds = obj.offboardComponentIds ?? [];
      obj.productPropertyIds = obj.productPropertyIds ?? [];
      obj.ownerIds = obj.ownerIds ?? [];
      obj.userFunctions = obj.userFunctions ?? [];
      obj.inputTypeValues = obj.inputTypeValues ?? [];
    }

    // --- TS: ensure alternative and defaults ---
    // Detect TS by singleUser or promptForMileage (TS-specific fields)
    if (obj.singleUser !== undefined || obj.promptForMileage !== undefined) {
      if (!obj.alternative) obj.alternative = { uid: '' };
      // If fpc/tpc are flat (new Go format), move into alternative
      if (obj.fpc !== undefined && !obj.alternative.fpc) { obj.alternative.fpc = obj.fpc; delete obj.fpc; }
      if (obj.tpc !== undefined && !obj.alternative.tpc) { obj.alternative.tpc = obj.tpc; delete obj.tpc; }
      obj.alternative.fpc = obj.alternative.fpc ?? '';
      obj.alternative.tpc = obj.alternative.tpc ?? '';
      // TS: normalize hierarchy link items (Go enriches with data wrapper)
      obj.testCases = (obj.testCases ?? []).map((link: any) => this.normalizeHierarchyLink(link));
      obj.testSuites = (obj.testSuites ?? []).map((link: any) => this.normalizeHierarchyLink(link));
      obj.ownerIds = obj.ownerIds ?? [];
      obj.userFunctionIds = obj.userFunctionIds ?? [];
      obj.offboardComponentIds = obj.offboardComponentIds ?? [];
      obj.productPropertyIds = obj.productPropertyIds ?? [];
    }

    // --- TA: nest flat fields into alternative ---
    // Detect TA by totalRiskLevel (TA-specific field)
    if (obj.totalRiskLevel !== undefined) {
      if (!obj.alternative) obj.alternative = { uid: '' };
      // Move ownerIds into alternative (TA alternative wraps ownerIds)
      if (obj.ownerIds !== undefined) { obj.alternative.ownerIds = obj.ownerIds; }
      obj.alternative.ownerIds = obj.alternative.ownerIds ?? [];
      // testedByGroups / testedByGroup → alternative.testedByGroupIds
      if (obj.testedByGroups !== undefined || obj.testedByGroup !== undefined) {
        obj.alternative.testedByGroupIds = obj.testedByGroups ?? obj.testedByGroup;
        delete obj.testedByGroups; delete obj.testedByGroup;
      }
      obj.alternative.testedByGroupIds = obj.alternative.testedByGroupIds ?? [];
      // testLevel (string) → alternative.testLevelIds (string[])
      if (obj.testLevel !== undefined) {
        obj.alternative.testLevelIds = Array.isArray(obj.testLevel) ? obj.testLevel : (obj.testLevel ? [obj.testLevel] : []);
        delete obj.testLevel;
      }
      obj.alternative.testLevelIds = obj.alternative.testLevelIds ?? [];
      // testCases → alternative.testCases (TA nests these)
      if (obj.testCases !== undefined) { obj.alternative.testCases = obj.testCases; delete obj.testCases; }
      // Go stores testCases as bare {uid, version} refs. Pad name/itemNo so the linked-item
      // table renders without crashing; the actual name/itemNo need server-side enrichment
      // (JOIN test_cases) in the Go testanalysis lambda before they'll display.
      obj.alternative.testCases = (obj.alternative.testCases ?? []).map((ref: any) => ({
        ...ref,
        name: ref?.name ?? '',
        itemNo: ref?.itemNo ?? '',
      }));
      // sopStart/sopEnd/specifications
      if (obj.sopStart !== undefined) { obj.alternative.sopStart = obj.sopStart; delete obj.sopStart; }
      if (obj.sopEnd !== undefined) { obj.alternative.sopEnd = obj.sopEnd; delete obj.sopEnd; }
      if (obj.specifications !== undefined) { obj.alternative.specifications = obj.specifications; delete obj.specifications; }
      obj.alternative.sopStart = obj.alternative.sopStart ?? '';
      obj.alternative.sopEnd = obj.alternative.sopEnd ?? '';
      obj.alternative.specifications = obj.alternative.specifications ?? [];
      // delegatedToTestLevels → alternative.excludedFromTest.delegatedToTestLevelIds
      if (Array.isArray(obj.delegatedToTestLevels) && obj.delegatedToTestLevels.length > 0) {
        obj.alternative.excludedFromTest = {
          ...(obj.alternative.excludedFromTest ?? {}),
          delegatedToTestLevelIds: obj.delegatedToTestLevels,
        };
      }
      delete obj.delegatedToTestLevels;
      // TA defaults
      obj.requirements = obj.requirements ?? [];
      obj.eeseRequirements = obj.eeseRequirements ?? [];
      obj.arpyRequirements = obj.arpyRequirements ?? [];
      // Older records stored specification.linkedRequirement as a string (polarionId); the UI
      // expects the requirement object. Reconstruct it from the TA's requirements when possible.
      const reqList: any[] = Array.isArray(obj.requirements) ? obj.requirements : [];
      obj.alternative.specifications = (obj.alternative.specifications ?? []).map((spec: any) => {
        if (spec && typeof spec.linkedRequirement === 'string' && spec.linkedRequirement) {
          const pid = spec.linkedRequirement;
          spec.linkedRequirement = reqList.find((r: any) => r?.polarionId === pid) ?? { polarionId: pid, title: '' };
        }
        return spec;
      });
    }

    // --- TSpec: pad testAnalyses refs so LinkedItemListComponent can render them ---
    // Go stores testAnalyses as bare {uid, version} refs (matches schema's TestAnalysisRef).
    // Old API returns full BaseTestItemDto with name/itemNo. Pad missing fields so the
    // table doesn't crash on `.name.length`. Names/itemNos remain blank until server-side
    // enrichment (JOIN with test_analyses) lands.
    if (Array.isArray(obj.testAnalyses)) {
      obj.testAnalyses = obj.testAnalyses.map((ref: any) => ({
        ...ref,
        name: ref?.name ?? '',
        itemNo: ref?.itemNo ?? '',
      }));
    }

    // --- Common defaults ---
    obj.comment = obj.comment ?? '';
    obj.lockedBy = obj.lockedBy ?? '';
    obj.approvalStatus = obj.approvalStatus ?? obj.approval?.status ?? '';
    obj.approvedForTestLevel = obj.approvedForTestLevel ?? '';
    obj.tags = obj.tags ?? [];
  }

  /**
   * Safely parse a date string from Go Lambda. Returns null for missing/invalid values.
   */
  private parseDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const d = new Date(value.endsWith('Z') ? value : value + 'Z');
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Filter out placeholder lockedBy values from seed data.
   * Real lock values are set by the Go Lambda lockItem mutation via setLockedByInData.
   */
  private sanitizeLockedBy(value: string | null | undefined): string {
    if (!value) return '';
    const placeholders = ['n/a', 'system_user', 'system', 'none', 'null'];
    if (placeholders.includes(value.toLowerCase())) return '';
    return value;
  }

  /**
   * Apply C# -> Go field name mappings (reverse of applyFieldNameMapping).
   * Operates on a camelCased object (after pascalToCamel conversion).
   */
  private applyReverseFieldNameMapping(obj: any, entity?: MigrationEntity): void {
    if (!obj || typeof obj !== 'object') return;

    // OwnerIds -> functionalGroupOwners
    if (obj.ownerIds !== undefined) {
      obj.functionalGroupOwners = obj.ownerIds;
      delete obj.ownerIds;
    }

    // EcuIds -> ecus — for the test items, whose Go schema calls the field `ecus`.
    // SW version coverage keeps `ecuIds`: that IS its Go field name, and its schema
    // (additionalProperties: false) would reject `ecus`.
    if (obj.ecuIds !== undefined && entity !== 'softwareVersionCoverage') {
      obj.ecus = obj.ecuIds;
      delete obj.ecuIds;
    }

    // OffboardComponentIds -> offboardComponents
    if (obj.offboardComponentIds !== undefined) {
      obj.offboardComponents = obj.offboardComponentIds;
      delete obj.offboardComponentIds;
    }

    // ProductPropertyIds -> productProperties
    if (obj.productPropertyIds !== undefined) {
      obj.productProperties = obj.productPropertyIds;
      delete obj.productPropertyIds;
    }

    // Alternative flattening is handled by cleanForGoSchema
  }
}
